'use client'

import { useImmer } from "@/app/lib/system/immer-wrapper"
import React, { SetStateAction, useRef, useContext, useEffect, useState } from "react";
// my imports
import Core from "@/app/components/Core";
import Menu from "@/app/components/Menu";
import PageWrapper from "@/app/components/PageWrapper";
import Hint, { HintProps, HintLink } from "@/app/components/Hint";
import { storyTutorialLevelData } from "@/app/lib/levels/tutorial";
import { AnyGameObj } from "@/app/lib/game-state/game-types";
import { StoryGameState } from "@/app/lib/game-state/story-game-state";
import UI, { useUI } from "@/app/lib/ui/ui";
import { Contexts } from "@/app/components/Context";
import { GameManager } from "@/app/lib/game-state/game-manager";
import { InternalRouter } from "@/app/lib/routing/internal-router";
import * as Util from "@/app/lib/system/util";
import { Phase } from "@/app/lib/levels/story-level-data-types";
import WordModule from "@/app/lib/game-state/word-module";

const INTERVAL_TIME = 800;

type TutorialEntry = {
    position: {word: number, letter: number} | {ingredient: number},
    text: string,
    linkAction?: {newPhase: number} | {special: 'cyclePhases' | 'endCycle'}
}

const tutorialEntries: TutorialEntry[] = [
    {
        position: {word: 2, letter: 1},
        text: "Welcome to Alphabet Soup Story Mode! Let's learn how it works."
    },
    {
        position: {word: 2, letter: 1},
        text: "Just like in the original mode, your job is to build words using ingredients. But with some important differences..."
    },
    {
        position: {word: 2, letter: 2},
        text: "First, puzzles are designed, not generated. There are a limited number to play."
    },
    {
        position: {word: 2, letter: 2},
        text: "Second, puzzles are themed. Solutions must not only fit the given letters, they must also fit the theme."
    },
    {
        position: {word: 2, letter: 3},
        text: "Lastly, puzzles are split into sections. This is one section.",
        linkAction: {newPhase: 1}
    },
    {
        position: {word: 3, letter: 2},
        text: "This is another.",
        linkAction: {newPhase: 2}
    },
    {
        position: {word: 5, letter: 2},
        text: "So is this!",
        linkAction: {special: 'cyclePhases'}
    },
    {
        position: {word: 5, letter: 3},
        text: "You can navigate between sections at any time, but note: each section has its own ingredients!",
        linkAction: {special: 'endCycle'}
    },
    {
        position: {word: 2, letter: 3},
        text: "That's it. Let's go to a story puzzle!",
    }
]

const startLinkText: string = 'Start→'
const nextLinkText: string = 'Next→';
const finishedLinkText: string = 'OK!';
const skipLinkText: string = 'Skip'

export default function StoryTutorial() {
    console.log('tutorialEntries at runtime:', tutorialEntries);

    const gameManager: GameManager | null = useContext(Contexts.GameManager);
    const ui: UI | null = useContext(Contexts.UI);
    if (!gameManager || !ui) {
        console.warn('bad context in StoryTutorial')
        return null;
    }

    const [state, update] = useImmer<StoryGameState>(new StoryGameState('story-tutorial-0.0'));
    const activeGame: AnyGameObj = {
        state,
        update: (recipe) => update(recipe as any)
    }

    // an active game ref that is necessary for the phase cycles to work
    const activeGameRef = useRef<AnyGameObj | null>(null);
    useEffect(() => {
        activeGameRef.current = activeGame;
    }, [activeGame]);

    // keeps track of our current hint
    // initialize to a negative to prevent loading yet
    const [allHintProps, setAllHintProps] = useState<HintProps[]>([]);
    const [currentHint, setCurrentHint] = useState(0);
    // loaded tracks the puzzle being built
    const [isLoaded, setIsLoaded] = useState(false);

    // keeps track of the interval for phase cycling:
    const phaseIntervalID = useRef<NodeJS.Timeout | null>(null);
    // cleanup:
    useEffect(() => {
        return () => {
            if (phaseIntervalID.current !== null) {
                clearInterval(phaseIntervalID.current);
            }
        }
    }, [])

    // build puzzle
    useEffect(() => {
        if (isLoaded)
            return;
        activeGame.update(state => {
            state.buildPuzzle(storyTutorialLevelData);
        });
        setIsLoaded(true);
        setCurrentHint(0);
    }, [isLoaded]);

    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        if (tutorialEntries.length < 1) {
            return;
        }

        if (ui.path !== '/story/tutorial') {
            return;
        }

        // remove focus from the game
        ui.defocusAll();

        // build the props
        const tempProps: HintProps[] = [];
        for (const [index, tutorialEntry] of tutorialEntries.entries()) {
            // start by getting the positional element
            const element: HTMLElement | null =
                ("word" in tutorialEntry.position)
                    ? ui.letterRefs.current[tutorialEntry.position.word][tutorialEntry.position.letter]
                    : ui.ingredientRefs.current[tutorialEntry.position.ingredient]
            if (!element) {
                continue;
            }
            // need order for hint links
            let order: 'first' | 'middle' | 'last' = 'middle';
            if (index === 0) {
                order = 'first';
            } else if (index === tutorialEntries.length - 1) {
                order = 'last';
            }
            // get the hint links
            const hintLinks: HintLink[] = getHintLinks(
                tutorialEntry,
                order,
                setCurrentHint,
                setIsLoaded,
                phaseIntervalID,
                gameManager,
                activeGameRef,
                ui
            );
            // now we're ready to push the props
            tempProps.push({
                element: element,
                text: tutorialEntry.text,
                links: hintLinks,
            })
        }
        setAllHintProps(tempProps);
    }, [isLoaded, tutorialEntries, ui.path])

    return(
        <>

        <Contexts.ActiveGame value={activeGame}>
        <PageWrapper
            activeGame={activeGame}
            ui={ui}
            handleInputs={false}
        >
            {allHintProps.length > 0 &&
                <Hint 
                    key={currentHint}
                    element={allHintProps[currentHint].element}
                    text={allHintProps[currentHint].text}
                    links={allHintProps[currentHint].links}
                    greyOverlay={true}
                />
            }
            <Core 
                subtitle='"tutorial"'
                isLoaded={isLoaded}
            />
            <Menu />
        </PageWrapper>
        </Contexts.ActiveGame>
        </>
    )
}

function getHintLinks(
    tutorialEntry: TutorialEntry,
    order: 'first' | 'last' | 'middle',
    setCurrentHint: React.Dispatch<SetStateAction<number>>,
    setIsLoaded: React.Dispatch<SetStateAction<boolean>>,
    phaseIntervalID: React.MutableRefObject<NodeJS.Timeout | null>,
    gameManager: GameManager,
    activeGameRef: React.MutableRefObject<AnyGameObj | null>,
    ui: UI
): HintLink[] {
    
    const activeGame = activeGameRef.current;
    if (!activeGame)
        return [];

    const hintLinks: HintLink[] = [];
    
    // if it's the first entry, push a skip link
    if (order === 'first') {
        hintLinks.push({
            text: skipLinkText,
            callback: () => {
                const iRouter = new InternalRouter(
                    gameManager,
                    ui
                );
                iRouter.goPath('/story');
                setIsLoaded(false);
            }
        })
    }

    // now figure out the text for the main link
    let text = '';
    if (order === 'first') {
        text = startLinkText;
    } else if (order === 'last') {
        text = finishedLinkText;
    } else {
        text = nextLinkText;
    }

    // and now the callback behavior
    let callback: () => void = () => {};

    // if it's the final hint, then route away
    if (order === 'last') {
        callback = () => {
            const iRouter = new InternalRouter(
                gameManager,
                ui
            );
            iRouter.goPath('/story');
            setIsLoaded(false);
        }
    }
    // otherwise, if we have a special action, accommodate it
    else if (tutorialEntry.linkAction) {
        // if the link action is reuqesting a new phase:
        if ("newPhase" in tutorialEntry.linkAction) {
            callback = () => {
                // increment the hint
                setCurrentHint(prev => prev + 1);
                // set the phase in the puzzle
                activeGame.update(state => {
                    if (!(state instanceof StoryGameState)) {
                        return;
                    }
                    if (
                        !tutorialEntry.linkAction
                        || !("newPhase" in tutorialEntry.linkAction)
                    ) {
                        return;
                    }
                    state.setCurrentPhase(tutorialEntry.linkAction.newPhase)
                })
            }
        }
        else if ("special" in tutorialEntry.linkAction) {
            if (tutorialEntry.linkAction.special === 'cyclePhases') {
                callback = () => {
                    setCurrentHint(prev => prev + 1);
                    startPhaseInterval(
                        activeGameRef,
                        ui,
                        phaseIntervalID,
                        INTERVAL_TIME
                    )
                }
            }
            if (tutorialEntry.linkAction.special === 'endCycle') {
                callback = () => {
                    setCurrentHint(prev => prev + 1);
                    stopPhaseInterval(
                        phaseIntervalID
                    )
                }
            }
        }
    }
    // otherwise the default of incrementing
    else {
        callback = () => {
            setCurrentHint(prev => prev + 1);
        }
    }

    hintLinks.push({
        text,
        callback
    })

    return hintLinks;
}

function startPhaseInterval(
    activeGameRef: React.MutableRefObject<AnyGameObj | null>,
    ui: UI,
    intervalIDRef: React.MutableRefObject<NodeJS.Timeout | null>,
    intervalTime: number,
) {
    function moveNextPhase() {
        const activeGame = activeGameRef.current;
        if (!activeGame)
            return;

        if (!(activeGame.state instanceof StoryGameState)) {
            return;
        }
        // we are going to move randomly to a phase that is not the current one
        const currentPhaseIndex: number = activeGame.state.currentPhase.index;
        const availablePhases: Phase[] =
            activeGame.state.phases.filter(p => p.index !== currentPhaseIndex);
        const nextPhase: Phase = Util.getRandom(availablePhases);
        // we are going to move there by randomly focusing a letterbox in that phase
        // and let the focus trigger the updater
        const nextWordIndex: number = Util.getRandom(nextPhase.wordIndices);
        const wordModule: WordModule = activeGame.state.words[nextWordIndex];
        const nextLetterPosition: number = Util.getRandom(wordModule.letters).position;
        // perform the focus
        ui.focus({wordIndex: nextWordIndex, letterPosition: nextLetterPosition})
    }

    // initial move right away
    moveNextPhase();

    intervalIDRef.current = setInterval(() => {
        moveNextPhase();
    }, intervalTime)
}

function stopPhaseInterval(
    intervalIDRef: React.MutableRefObject<NodeJS.Timeout | null>,
) {
    if (intervalIDRef.current) {
        clearInterval(intervalIDRef.current);
    }
}